import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/vue';
import baseApiUrl from './BaseApiUrl';
import { authorization } from './Autorizathion';

// ─── Cola para requests que llegaron mientras hay un refresh en curso ─────────
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  failedQueue = [];
};

let forbiddenMessage: string | null = null;

export const takeForbiddenMessage = (): string | null => {
  const message = forbiddenMessage;
  forbiddenMessage = null;
  return message;
};

// ─── Instancia principal ──────────────────────────────────────────────────────
const $http: AxiosInstance = axios.create({
  baseURL: baseApiUrl,
  withCredentials: true,
});

// ─── Request interceptor: adjunta el access token desde Pinia ────────────────
$http.interceptors.request.use(
  async (config: AxiosRequestConfig) => {
    forbiddenMessage = null;
    config.headers = config.headers ?? {};
    const auth = authorization();
    if (auth) {
      config.headers.Authorization = auth;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Métodos seguros de reintentar a ciegas: no mutan datos, así que repetirlos
// tras un refresh de token no puede duplicar nada.
const SAFE_METHODS = new Set(['get', 'head', 'options']);

// Da forma consistente al error antes de propagarlo -mismo shape sin
// importar qué rama del interceptor lo generó- y lo reporta a Sentry.
// Antes, las rutas de salida temprana (refresh fallido, escritura bloqueada)
// rechazaban con el AxiosError crudo, distinto del `error.response.data`
// que usa el resto de la app (p.ej. `error.errors`/`error.message` en
// Login.vue), y sin quedar registradas en Sentry.
const parseRetryAfter = (value: unknown): number | undefined => {
  const seconds = Number(value);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
};

const finalizeError = (error: any) => {
  if (axios.isAxiosError(error)) {
    Sentry.withScope((scope) => {
      scope.setTag('api.method', (error.config?.method ?? 'unknown').toUpperCase());
      scope.setTag('api.status', String(error.response?.status ?? 0));
      scope.setContext('API Request', {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        data: error.config?.data,
      });
      scope.setContext('API Response', { body: error.response?.data });
      Sentry.captureException(error);
    });

    // El status y el Retry-After viajan junto al body: sin ellos el componente
    // no puede distinguir un 429 de cualquier otro error ni saber cuánto dura.
    const details = {
      status: error.response?.status,
      retryAfterSeconds: parseRetryAfter(error.response?.headers?.['retry-after']),
    };
    const body = error.response?.data;

    return Promise.reject(
      body && typeof body === 'object' ? { ...body, ...details } : details,
    );
  }
  return Promise.reject({ error: { message: 'Error de peticion' } });
};

// ─── Response interceptor: maneja 401 con renovación automática ──────────────
$http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const isSafeMethod = SAFE_METHODS.has((originalRequest.method ?? 'get').toLowerCase());
    // El access token vive solo en memoria (Pinia) y el request interceptor
    // únicamente adjunta Authorization cuando hay uno. Si este request nunca
    // llevó token (landing pública, donde el usuario no necesita loguearse),
    // un 401 no tiene nada que ver con una sesión expirada: no hay nada que
    // refrescar, así que jamás debe intentarse.
    const hadAuthHeader = !!originalRequest.headers?.Authorization;

    if (
      error.response?.status === 401 &&
      hadAuthHeader &&
      !originalRequest._retry &&
      originalRequest.url !== 'auth/refresh' &&
      originalRequest.url !== 'auth/login' &&
      originalRequest.url !== 'auth/login2fa'
    ) {
      // Si ya hay un refresh en curso, encolar este request y esperar
      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            // Un POST/PUT/PATCH/DELETE nunca se reenvía solo: si ya había
            // impactado el backend, reintentarlo a ciegas duplicaría el
            // registro. Propagamos el error original (con el mismo shape
            // que el resto de los errores) para que el reintento sea una
            // acción explícita del usuario.
            if (!isSafeMethod) {
              return finalizeError(error);
            }
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return $http(originalRequest);
          })
          .catch((err) => finalizeError(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // La cookie HttpOnly se envía automáticamente gracias a withCredentials: true
        const { data } = await $http.post('auth/refresh');

        // Actualizar el store con el nuevo access token
        const { useAuthStore } = await import('@store/auth');
        const store = useAuthStore();
        store.refresh({ token: data.token, user: data.user });

        processQueue(null, data.token);

        // El token ya quedó renovado para las próximas peticiones. Pero NO
        // reintentamos automáticamente escrituras (POST/PUT/PATCH/DELETE):
        // si el request original ya había creado/modificado el registro en
        // el backend (o si el 401 llegó por otro motivo), reenviarlo a
        // ciegas duplicaría datos -por ejemplo, una póliza-. Solo los
        // métodos de lectura son seguros de reintentar solos.
        if (!isSafeMethod) {
          return finalizeError(error);
        }

        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${data.token}`;
        return $http(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // El refresh falló: sesión terminada, redirigir a login
        const { useAuthStore } = await import('@store/auth');
        const store = useAuthStore();
        store.logout();
        window.location.href = '/login';
        return finalizeError(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 403) {
      forbiddenMessage = (error.response.data as { message?: string })?.message ?? null;
    }

    // Errores que no son 401, que no tienen sesión activa, o que ya se reintentaron
    return finalizeError(error);
  },
);

export default $http;
